import { useState } from 'react'
import {
  Button, Card, Form, Input, Modal, Popconfirm, Select, Space,
  Table, Tag, Typography, message, Alert, Descriptions
} from 'antd'
import { PlusOutlined, DeleteOutlined, EyeOutlined, CopyOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useDeviceList, useRegisterDevice, useDeleteDevice } from '../hooks/useDevices'
import { useDeviceProfiles } from '../hooks/useDeviceProfiles'
import { HeartbeatBadge } from '../components/HeartbeatBadge'
import type { Device } from '../api/devices'

const { Title } = Typography

export default function DeviceList() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [registerModalOpen, setRegisterModalOpen] = useState(false)
  const [newDeviceInfo, setNewDeviceInfo] = useState<{ name: string; api_key: string } | null>(null)
  const [form] = Form.useForm()

  const { data, isLoading, refetch } = useDeviceList({ page, status: statusFilter })
  const { data: profiles } = useDeviceProfiles()
  const registerMutation = useRegisterDevice()
  const deleteMutation = useDeleteDevice()

  const profileMap: Record<string, string> = {}
  if (profiles) {
    for (const p of profiles) {
      profileMap[p.id] = p.display_name
    }
  }

  const handleRegister = async () => {
    try {
      const values = await form.validateFields()
      const res = await registerMutation.mutateAsync(values)
      setNewDeviceInfo({ name: res.name, api_key: res.api_key })
      form.resetFields()
    } catch {
      // validation error
    }
  }

  const handleCloseRegister = () => {
    setRegisterModalOpen(false)
    setNewDeviceInfo(null)
    if (newDeviceInfo) refetch()
  }

  const columns = [
    { title: '设备名称', dataIndex: 'name', key: 'name' },
    {
      title: '型号',
      dataIndex: 'profile_id',
      key: 'profile_id',
      render: (pid: string | null) =>
        pid ? <Tag color="blue">{profileMap[pid] || pid}</Tag> : '—',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (_: string, record: Device) => (
        <HeartbeatBadge status={record.status} lastSeen={record.last_seen} />
      ),
    },
    {
      title: '最后活跃',
      dataIndex: 'last_seen',
      key: 'last_seen',
      render: (t?: string) => t ? new Date(t).toLocaleString('zh-CN') : '—',
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (t: string) => new Date(t).toLocaleDateString('zh-CN'),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_: unknown, record: Device) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/devices/${record.id}`)}
          >
            详情
          </Button>
          <Popconfirm
            title="确定删除此设备？"
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="删除"
            cancelText="取消"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>设备管理</Title>
        <Space>
          <Select
            placeholder="状态筛选"
            allowClear
            style={{ width: 120 }}
            onChange={setStatusFilter}
            options={[
              { value: 'online', label: '在线' },
              { value: 'offline', label: '离线' },
            ]}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setRegisterModalOpen(true)}
          >
            注册设备
          </Button>
        </Space>
      </div>

      <Card>
        <Table
          dataSource={data?.items}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: data?.total,
            pageSize: 20,
            current: page,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 台设备`,
          }}
          locale={{ emptyText: '暂无设备，点击"注册设备"添加' }}
        />
      </Card>

      <Modal
        title="注册新设备"
        open={registerModalOpen}
        onOk={newDeviceInfo ? handleCloseRegister : handleRegister}
        onCancel={handleCloseRegister}
        okText={newDeviceInfo ? '完成' : '注册'}
        cancelText={newDeviceInfo ? null : '取消'}
        cancelButtonProps={newDeviceInfo ? { style: { display: 'none' } } : {}}
        confirmLoading={registerMutation.isPending}
        width={480}
      >
        {newDeviceInfo ? (
          <div>
            <Alert
              type="success"
              message="设备注册成功！"
              description="请立即保存以下 API Key，它只会显示一次。"
              style={{ marginBottom: 16 }}
            />
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="设备名称">{newDeviceInfo.name}</Descriptions.Item>
              <Descriptions.Item label="API Key">
                <Space>
                  <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{newDeviceInfo.api_key}</code>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(newDeviceInfo.api_key)
                      message.success('已复制')
                    }}
                  >
                    复制
                  </Button>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </div>
        ) : (
          <Form form={form} layout="vertical">
            <Form.Item name="name" label="设备名称" rules={[{ required: true, message: '请输入设备名称' }]}>
              <Input placeholder="如：客厅音箱、门口摄像头" />
            </Form.Item>
            <Form.Item name="profile_id" label="设备型号" rules={[{ required: true, message: '请选择设备型号' }]}>
              <Select
                placeholder="选择设备型号"
                options={profiles?.map((p) => ({
                  value: p.id,
                  label: `${p.display_name} (${p.category === 'sensor' ? '传感器' : '执行器'})`,
                }))}
              />
            </Form.Item>
            <Form.Item name="description" label="描述（可选）">
              <Input.TextArea rows={2} placeholder="设备描述" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  )
}
